import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { telechargeFichier, useRequete } from '../api/client';
import type { ApercuRadars, Couverture, Equipe, GrilleEquipe, Tendances } from '../api/types';
import { useSession } from '../auth/session';
import {
  BarreNiveau,
  GraphiqueLignes,
  Sparkline,
  TEINTES_SERIES,
  Variation,
  type Serie,
} from '../composants/graphiques';
import { Anneau, Badge, Chargement, Equipes, Message } from '../composants/elements';
import { LEGENDE, celluleDe } from '../composants/niveaux';

/**
 * Les dashboards d'encadrement — implémentation de
 * « Skills Matrix Admin Dashboards.dc.html ».
 *
 * La maquette proposait trois variantes ; elles répondent à trois questions
 * différentes, donc les trois sont là, sous forme d'onglets :
 *
 *   Coverage  (1c) le tableau d'ensemble, trié par risque — la page d'entrée
 *   Explorer  (1a) une technologie ou quatre, suivies dans le temps
 *   People    (1b) la trajectoire d'une personne, compétence par compétence
 *
 * Deux écarts avec la maquette : les niveaux vont de 0 à 5 (échelle validée,
 * §4.1) et non de « Aware » à « Mentor » ; et « profiles up to date » est
 * remplacé par le taux de fiches soumises, qui est ce que la base sait dire.
 */

type Onglet = 'coverage' | 'grid' | 'explorer' | 'people';

const ONGLETS: { cle: Onglet; libelle: string; sous: string }[] = [
  { cle: 'coverage', libelle: 'Coverage', sous: 'Everything at a glance, sorted by risk' },
  { cle: 'grid', libelle: 'Grid', sous: 'One grid, per team — who holds what, at what level' },
  { cle: 'explorer', libelle: 'Explorer', sous: 'One trend chart, technology by technology' },
  { cle: 'people', libelle: 'People', sous: 'One person’s trajectory, skill by skill' },
];

function Kpi({
  valeur,
  libelle,
  alerte,
}: {
  valeur: string | number;
  libelle: string;
  alerte?: boolean;
}): JSX.Element {
  return (
    <div className="kpi">
      <div className="kpi__valeur" style={alerte ? { color: 'var(--danger)' } : undefined}>
        {valeur}
      </div>
      <div className="kpi__libelle">{libelle}</div>
    </div>
  );
}

export function Dashboards(): JSX.Element {
  const [onglet, setOnglet] = useState<Onglet>('coverage');

  return (
    <div className="contenu" style={{ paddingBottom: 'var(--space-9)' }}>
      <div className="entete-page">
        <div>
          <p className="eyebrow" style={{ marginBottom: 14 }}>
            Management view
          </p>
          <h1 style={{ fontSize: 'clamp(28px, 3.4vw, 44px)' }}>Capability dashboards</h1>
          <p className="plomb" style={{ marginTop: 14, maxWidth: 620 }}>
            {ONGLETS.find((o) => o.cle === onglet)?.sous}. Self-declared levels, with written
            evidence required from level 3 up.
          </p>
        </div>
      </div>

      <div className="onglets" role="tablist">
        {ONGLETS.map((o) => (
          <button
            key={o.cle}
            type="button"
            role="tab"
            aria-selected={onglet === o.cle}
            className={`onglet${onglet === o.cle ? ' onglet--actif' : ''}`}
            onClick={() => setOnglet(o.cle)}
          >
            {o.libelle}
          </button>
        ))}
      </div>

      <div style={{ paddingTop: 28 }}>
        {onglet === 'coverage' ? <TableauCouverture /> : null}
        {onglet === 'grid' ? <Grille /> : null}
        {onglet === 'explorer' ? <Explorateur /> : null}
        {onglet === 'people' ? <Personnes /> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1c — Coverage board
// ---------------------------------------------------------------------------

function TableauCouverture(): JSX.Element {
  const [radar, setRadar] = useState('');
  const apercu = useRequete<ApercuRadars>('/api/radars');
  const donnees = useRequete<Couverture>(
    `/api/dashboards/couverture${radar ? `?radar=${encodeURIComponent(radar)}` : ''}`,
    [radar],
  );

  if (donnees.erreur) return <Message variante="erreur">{donnees.erreur.message}</Message>;
  if (!donnees.donnees) return <Chargement quoi="Building the board" />;

  const c = donnees.donnees;

  // Ce qui bouge le plus, en tête : c'est la lecture du trimestre.
  const cartes = [...c.technologies]
    .filter((t) => t.couverture > 0 || t.variation !== 0)
    .sort((a, b) => Math.abs(b.variation) - Math.abs(a.variation) || b.couverture - a.couverture)
    .slice(0, 8);

  return (
    <>
      <div className="barre-actions" style={{ justifyContent: 'space-between', paddingBottom: 20 }}>
        <div className="barre-actions">
          <select
            className="selection"
            style={{ width: 'auto' }}
            value={radar}
            onChange={(e) => setRadar(e.target.value)}
            aria-label="Filter by radar"
          >
            <option value="">All radars</option>
            {(apercu.donnees?.radars ?? []).map((r) => (
              <option key={r.radar} value={r.radar}>
                {r.libelle}
              </option>
            ))}
          </select>
          <span className="discret">
            {c.trimestres.length} quarter(s) · current {c.trimestre}
          </span>
        </div>
        <button
          type="button"
          className="bouton bouton--primaire bouton--petit"
          onClick={() =>
            void telechargeFichier(
              `/api/admin/campagnes/${c.trimestre}/export.csv`,
              `skills-elyadata-${c.trimestre}.csv`,
            ).catch(() => undefined)
          }
        >
          Export CSV
        </button>
      </div>

      <div className="bande-kpis">
        <Kpi valeur={c.kpis.personnes} libelle="Collaborators" />
        <Kpi valeur={c.kpis.technologies} libelle="Technologies tracked" />
        <Kpi valeur={c.kpis.concentrees} libelle="Concentrated skills" alerte={c.kpis.concentrees > 0} />
        <Kpi valeur={`${c.kpis.pourcentageAJour}%`} libelle="Profiles submitted" />
      </div>

      <div className="petits-multiples">
        {cartes.map((t) => {
          const teinte =
            t.variation < 0
              ? 'var(--danger)'
              : t.anneau === 'adopt'
                ? '#004F99'
                : t.anneau === 'trial'
                  ? '#05BAEE'
                  : t.anneau === 'assess'
                    ? '#D6007F'
                    : '#C4CADF';
          return (
            <div
              key={t.technologieId}
              className="petit-multiple"
              style={t.variation < 0 ? { background: '#FFF7FB' } : undefined}
            >
              <div className="petit-multiple__titre">
                <span>{t.titre}</span>
                <Variation valeur={t.variation} />
              </div>
              <Sparkline
                valeurs={t.points.map((p) => p.couverture)}
                teinte={teinte}
                titre={`${t.titre}: coverage over ${t.points.length} quarters`}
              />
              <div className="petit-multiple__pied">
                <span>{t.couverture} people</span>
                <span>avg {t.moyenne ?? '—'}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="deux-colonnes">
        <section>
          <h4 className="titre-section">Concentration risk</h4>
          {c.concentration.length === 0 ? (
            <p className="discret">
              No skill rests on one or two people. That is rarer than it sounds.
            </p>
          ) : (
            <table className="tableau">
              <tbody>
                {c.concentration.map((l) => (
                  <tr key={l.technologieId}>
                    <td>{l.titre}</td>
                    <td>
                      <Anneau anneau={l.anneau} />
                    </td>
                    <td className="mono discret" style={{ whiteSpace: 'nowrap' }}>
                      {l.porteurs} holder{l.porteurs > 1 ? 's' : ''}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Badge variante={l.criticite === 'critique' ? 'risque' : 'incomplet'}>
                        {l.criticite === 'critique' ? 'critical' : 'watch'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section>
          <h4 className="titre-section">
            Biggest movers {c.trimestrePrecedent ? `since ${c.trimestrePrecedent}` : 'this quarter'}
          </h4>
          {c.mouvements.length === 0 ? (
            <p className="discret">
              Nothing moved yet this quarter — the current campaign is still being filled in.
            </p>
          ) : (
            <table className="tableau">
              <tbody>
                {c.mouvements.map((m) => (
                  <tr key={`${m.personneId}-${m.technologieId}`}>
                    <td style={{ width: 40 }}>
                      <span className="pastille">{m.initiales}</span>
                    </td>
                    <td>
                      <Link to={`/personnes/${m.personneId}`}>{m.nom}</Link>
                      <span className="discret"> · {m.titre}</span>
                      <br />
                      <span className="discret">
                        {m.avant === null ? 'first declaration' : `level ${m.avant}`} → level {m.apres}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Variation valeur={m.delta} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// « One grid, per team »
// ---------------------------------------------------------------------------

/**
 * La grille équipe × technologies. Un groupe à la fois : 304 colonnes ne se
 * lisent pas, et c'est déjà le découpage par lequel on saisit (§6.1).
 */
function Grille(): JSX.Element {
  const equipes = useRequete<{ equipes: Equipe[] }>('/api/equipes');
  const [equipeId, setEquipeId] = useState('');
  const [tag, setTag] = useState('');
  const [groupe, setGroupe] = useState('');

  const toutes = equipes.donnees?.equipes ?? [];

  /** Les étiquettes réellement portées par des équipes, avec leur libellé. */
  const tagsDisponibles = [
    ...new Map(
      toutes.flatMap((e) => e.etiquettes.map((t) => [t.tag, t.libelle] as const)),
    ).entries(),
  ];

  const filtrees = tag ? toutes.filter((e) => e.tags.includes(tag)) : toutes;

  /**
   * Si l'équipe choisie ne porte pas l'étiquette filtrée, on retombe sur la
   * première qui la porte — laisser une sélection invisible dans la liste est
   * plus déroutant que de la déplacer.
   */
  const choisie = filtrees.some((e) => e.id === equipeId)
    ? equipeId
    : filtrees[0]?.id || '';
  const requete = new URLSearchParams();
  if (choisie) requete.set('equipeId', choisie);
  if (groupe) requete.set('groupe', groupe);

  const grille = useRequete<GrilleEquipe>(
    choisie ? `/api/dashboards/grille?${requete.toString()}` : null,
    [choisie, groupe],
  );

  if ((equipes.donnees?.equipes ?? []).length === 0 && !equipes.enCours) {
    return (
      <Message variante="attention">
        No team yet. Create a chapter, a project or a guild in Admin → Teams, then add people to it —
        the grid reads one team at a time.
      </Message>
    );
  }

  const g = grille.donnees;

  /**
   * Les sélecteurs sont rendus quoi qu'il arrive à la grille : une erreur ou une
   * équipe vide ne doit pas retirer de l'écran le moyen d'en choisir une autre.
   */
  return (
    <>
      <div className="barre-actions" style={{ paddingBottom: 20 }}>
        {tagsDisponibles.length > 0 ? (
          <div className="barre-actions" style={{ gap: 4 }}>
            <button
              type="button"
              className={`puce${tag === '' ? ' puce--active' : ''}`}
              aria-pressed={tag === ''}
              onClick={() => setTag('')}
            >
              All
            </button>
            {tagsDisponibles.map(([cle, libelle]) => (
              <button
                key={cle}
                type="button"
                className={`puce${tag === cle ? ' puce--active' : ''}`}
                aria-pressed={tag === cle}
                onClick={() => {
                  setTag(cle);
                  setGroupe('');
                }}
              >
                {libelle}
              </button>
            ))}
          </div>
        ) : null}

        <select
          className="selection"
          style={{ width: 'auto' }}
          value={choisie}
          onChange={(e) => {
            setEquipeId(e.target.value);
            setGroupe('');
          }}
          aria-label="Team"
        >
          {filtrees.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nom}
              {e.tags.length > 0 ? ` — ${e.etiquettes.map((t) => t.libelle).join(', ')}` : ''}
              {e.membres === 0 ? ' · empty' : ` · ${e.membres}`}
            </option>
          ))}
        </select>

        {g ? (
          <select
            className="selection"
            style={{ width: 'auto' }}
            value={groupe || g.groupe.cle}
            onChange={(e) => setGroupe(e.target.value)}
            aria-label="Technology group"
          >
            {g.groupesDisponibles.map((gr) => (
              <option key={gr.cle} value={gr.cle}>
                {gr.libelleRadar} · {gr.libelle} ({gr.technologies})
              </option>
            ))}
          </select>
        ) : null}

        {g ? (
          <span className="discret">
            {g.resume.couvertes} of {g.resume.couvertes + g.resume.nonCouvertes} covered at level 3+ ·{' '}
            {g.resume.cellulesRepondues}/{g.resume.cellules} cells answered
          </span>
        ) : null}
      </div>

      {grille.erreur ? <Message variante="erreur">{grille.erreur.message}</Message> : null}
      {!g && !grille.erreur ? <Chargement quoi="Building the grid" /> : null}

      {g && g.personnes.length === 0 ? (
        <Message variante="attention">
          Nobody is in {g.equipe} yet. Add people to it in Admin → People — the grid reads one team
          at a time.
        </Message>
      ) : null}

      {g && g.personnes.length > 0 ? (
      <div className="grille-apercu">
        <div className="grille-apercu__entete">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-title)', fontSize: 15, fontWeight: 600 }}>
              {g.equipe}
            </span>
            {g.libellesTags.map((t) => (
              <span key={t} className="jeton">
                {t}
              </span>
            ))}
            <span className="jeton">
              {g.personnes.length} {g.personnes.length === 1 ? 'person' : 'people'}
            </span>
          </div>
          <span className="mono discret" style={{ fontSize: 12 }}>
            {g.groupe.libelleRadar} · {g.groupe.libelle} · {g.trimestre}
          </span>
        </div>

        <div className="grille-apercu__defilement">
          <div
            className="grille-apercu__corps"
            style={{ minWidth: Math.max(760, 240 + g.technologies.length * 62) }}
          >
            <div
              className="grille-apercu__ligne grille-apercu__ligne--titres"
              style={{
                gridTemplateColumns: `minmax(210px, 1.6fr) repeat(${g.technologies.length}, minmax(56px, 1fr))`,
              }}
            >
              <span className="grille-apercu__libelle">Collaborator</span>
              {g.technologies.map((t) => (
                <span key={t.id} className="grille-apercu__techno" title={`${t.titre} — ${t.anneau}`}>
                  {t.titre}
                </span>
              ))}
            </div>

            {g.lignes.map((ligne) => {
              const personne = g.personnes.find((p) => p.id === ligne.personneId);
              return (
                <div
                  key={ligne.personneId}
                  className="grille-apercu__ligne"
                  style={{
                    gridTemplateColumns: `minmax(210px, 1.6fr) repeat(${g.technologies.length}, minmax(56px, 1fr))`,
                  }}
                >
                  <div className="grille-apercu__personne">
                    <span className="grille-apercu__pastille">{personne?.initiales}</span>
                    <span style={{ minWidth: 0 }}>
                      <Link to={`/personnes/${ligne.personneId}`} className="grille-apercu__nom">
                        {personne?.nom}
                      </Link>
                      <span className="grille-apercu__poste">
                        {personne?.intitulePoste ?? '—'}
                      </span>
                    </span>
                  </div>

                  {ligne.cellules.map((cellule, index) => {
                    const technologie = g.technologies[index];
                    const c = celluleDe(
                      cellule.niveau,
                      cellule.nonConcernee ? 'non_concernee' : cellule.niveau === null ? null : 'notee',
                    );
                    return (
                      <span
                        key={technologie?.id ?? index}
                        className="grille-apercu__cellule"
                        style={{
                          background: c.fond,
                          borderColor: cellule.aRevoir ? 'var(--brand-cyan-600)' : c.bord,
                          borderStyle: cellule.aRevoir ? 'dashed' : 'solid',
                        }}
                        title={`${personne?.nom} · ${technologie?.titre} — ${c.libelle}${cellule.aRevoir ? ' (carried over, not reviewed)' : ''}`}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grille-apercu__legende">
          <span className="champ__libelle">Legend</span>
          {LEGENDE.map((l) => (
            <span key={l.libelle} className="legende__entree">
              <span
                className="legende__pastille"
                style={{ background: l.fond, borderColor: l.bord }}
                aria-hidden="true"
              />
              {l.libelle}
            </span>
          ))}
          <span className="legende__entree">
            <span
              className="legende__pastille"
              style={{ background: 'transparent', borderColor: 'var(--brand-cyan-600)', borderStyle: 'dashed' }}
              aria-hidden="true"
            />
            carried over, not reviewed
          </span>
        </div>
      </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// 1a — Technology explorer
// ---------------------------------------------------------------------------

const MAX_SELECTION = 4;

function Explorateur(): JSX.Element {
  const [fenetre, setFenetre] = useState(8);
  const [radar, setRadar] = useState('');
  const [equipe, setEquipe] = useState('');
  const [selection, setSelection] = useState<string[]>([]);
  const [filtre, setFiltre] = useState('');
  const [mesure, setMesure] = useState<'couverture' | 'moyenne' | 'experts'>('couverture');

  const apercu = useRequete<ApercuRadars>('/api/radars');
  const listeEquipes = useRequete<{ equipes: Equipe[] }>('/api/equipes');

  const requete = new URLSearchParams({ trimestres: String(fenetre) });
  if (radar) requete.set('radar', radar);
  if (equipe) requete.set('equipeId', equipe);

  const donnees = useRequete<Tendances>(`/api/dashboards/tendances?${requete.toString()}`, [
    fenetre,
    radar,
    equipe,
  ]);

  const technologies = donnees.donnees?.technologies ?? [];

  const choisies = useMemo(() => {
    if (selection.length > 0) {
      return technologies.filter((t) => selection.includes(t.technologieId));
    }
    return technologies.slice(0, MAX_SELECTION);
  }, [technologies, selection]);

  const visibles = useMemo(() => {
    const terme = filtre.trim().toLowerCase();
    const liste = terme
      ? technologies.filter((t) => t.titre.toLowerCase().includes(terme))
      : technologies;
    return liste.slice(0, 40);
  }, [technologies, filtre]);

  if (donnees.erreur) return <Message variante="erreur">{donnees.erreur.message}</Message>;
  if (!donnees.donnees) return <Chargement quoi="Loading trends" />;

  const etiquettes = donnees.donnees.trimestres.map((t) => t.replace('-Q', ' Q'));

  const series: Serie[] = choisies.map((t, index) => ({
    cle: t.technologieId,
    libelle: t.titre,
    teinte: TEINTES_SERIES[index % TEINTES_SERIES.length]!,
    valeurs: t.points.map((p) =>
      mesure === 'couverture' ? p.couverture : mesure === 'experts' ? p.experts : p.moyenne,
    ),
  }));

  const titreMesure =
    mesure === 'couverture'
      ? 'People at level 3 or above'
      : mesure === 'experts'
        ? 'People at level 4 or above'
        : 'Average declared level';

  function basculer(id: string): void {
    setSelection((actuelle) => {
      if (actuelle.includes(id)) return actuelle.filter((x) => x !== id);
      if (actuelle.length >= MAX_SELECTION) return [...actuelle.slice(1), id];
      return [...actuelle, id];
    });
  }

  return (
    <>
      <div className="barre-actions" style={{ paddingBottom: 20 }}>
        <select
          className="selection"
          style={{ width: 'auto' }}
          value={fenetre}
          onChange={(e) => setFenetre(Number(e.target.value))}
          aria-label="Time window"
        >
          <option value={4}>Last 4 quarters</option>
          <option value={8}>Last 8 quarters</option>
          <option value={12}>Last 12 quarters</option>
        </select>
        <select
          className="selection"
          style={{ width: 'auto' }}
          value={radar}
          onChange={(e) => setRadar(e.target.value)}
          aria-label="Filter by radar"
        >
          <option value="">All radars</option>
          {(apercu.donnees?.radars ?? []).map((r) => (
            <option key={r.radar} value={r.radar}>
              {r.libelle}
            </option>
          ))}
        </select>
        <select
          className="selection"
          style={{ width: 'auto' }}
          value={equipe}
          onChange={(e) => setEquipe(e.target.value)}
          aria-label="Filter by team"
        >
          <option value="">All teams</option>
          {(listeEquipes.donnees?.equipes ?? []).map((e) => (
            <option key={e.id} value={e.id}>
              {e.nom}
            </option>
          ))}
        </select>
      </div>

      <div className="explorateur">
        <aside className="explorateur__liste">
          <div className="explorateur__entete">
            <span>Technologies · {technologies.length}</span>
            <input
              className="saisie"
              style={{ marginTop: 10 }}
              placeholder="Search"
              value={filtre}
              onChange={(e) => setFiltre(e.target.value)}
              aria-label="Search technologies"
            />
          </div>

          {visibles.map((t) => {
            const index = choisies.findIndex((c) => c.technologieId === t.technologieId);
            const active = index !== -1;
            return (
              <button
                key={t.technologieId}
                type="button"
                className={`explorateur__ligne${active ? ' explorateur__ligne--active' : ''}`}
                onClick={() => basculer(t.technologieId)}
                aria-pressed={active}
              >
                <span className="explorateur__nom">
                  <span
                    className="explorateur__point"
                    style={{
                      background: active
                        ? TEINTES_SERIES[index % TEINTES_SERIES.length]
                        : 'transparent',
                    }}
                    aria-hidden="true"
                  />
                  {t.titre}
                </span>
                <span className="mono discret">{t.couverture}</span>
                <Variation valeur={t.variation} />
              </button>
            );
          })}

          {technologies.length > visibles.length ? (
            <p className="explorateur__pied discret">
              {technologies.length - visibles.length} more — narrow the search
            </p>
          ) : null}
        </aside>

        <section className="explorateur__graphique">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <div>
              <h3 style={{ fontSize: 20 }}>{titreMesure}</h3>
              <p className="discret" style={{ marginTop: 6 }}>
                {choisies.length} technolog{choisies.length === 1 ? 'y' : 'ies'} selected · quarterly
                snapshots
              </p>
            </div>
            <div className="barre-actions">
              {(
                [
                  ['couverture', 'Level 3+'],
                  ['experts', 'Level 4+'],
                  ['moyenne', 'Avg. level'],
                ] as const
              ).map(([cle, libelle]) => (
                <button
                  key={cle}
                  type="button"
                  className={`puce${mesure === cle ? ' puce--active' : ''}`}
                  onClick={() => setMesure(cle)}
                  aria-pressed={mesure === cle}
                >
                  {libelle}
                </button>
              ))}
            </div>
          </div>

          <GraphiqueLignes
            series={series}
            etiquettes={etiquettes}
            max={mesure === 'moyenne' ? 5 : undefined}
          />

          <div className="legende-series">
            {series.map((s) => (
              <span key={s.cle} className="legende__entree">
                <span className="legende__trait" style={{ background: s.teinte }} aria-hidden="true" />
                {s.libelle}
              </span>
            ))}
          </div>

          <div className="bande-kpis bande-kpis--plate">
            <Kpi
              valeur={
                donnees.donnees.resume.variation >= 0
                  ? `+${donnees.donnees.resume.variation}`
                  : String(donnees.donnees.resume.variation)
              }
              libelle={`Coverage change, ${donnees.donnees.trimestres.length} quarters`}
            />
            <Kpi valeur={donnees.donnees.resume.moyenne ?? '—'} libelle="Average level" />
            <Kpi valeur={donnees.donnees.resume.couverture} libelle="Declarations at 3+" />
            <Kpi valeur={donnees.donnees.resume.referents} libelle="Level 5 named" />
          </div>
        </section>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// 1b — People
// ---------------------------------------------------------------------------

function Personnes(): JSX.Element {
  const session = useSession();
  const liste = useRequete<{
    personnes: { id: string; nom: string; equipes: { nom: string }[]; intitulePoste: string | null }[];
  }>('/api/personnes');
  const [choisie, setChoisie] = useState<string | null>(null);

  const personnes = liste.donnees?.personnes ?? [];
  const active = choisie ?? personnes[0]?.id ?? null;

  return (
    <div className="explorateur">
      <aside className="explorateur__liste">
        <div className="explorateur__entete">
          <span>People · {personnes.length}</span>
        </div>
        {personnes.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`explorateur__ligne explorateur__ligne--empilee${p.id === active ? ' explorateur__ligne--active' : ''}`}
            onClick={() => setChoisie(p.id)}
            aria-pressed={p.id === active}
          >
            <span className="explorateur__nom">
              {p.nom}
              {p.id === session.moi?.personne.id ? <span className="discret"> · you</span> : null}
            </span>
            <Equipes equipes={p.equipes} max={2} />
          </button>
        ))}
      </aside>

      <section>{active ? <Trajectoire personneId={active} /> : <p className="vide">No one yet.</p>}</section>
    </div>
  );
}

type TrajectoireVue = {
  personne: {
    id: string;
    nom: string;
    initiales: string;
    email: string;
    equipes: { id: string; nom: string; tags: string[]; libellesTags: string[] }[];
    intitulePoste: string | null;
    depuis: string;
  };
  trimestres: string[];
  kpis: { declarees: number; moyenne: number | null; referentDe: number; variationMoyenne: number | null };
  competences: {
    technologieId: string;
    titre: string;
    radar: string;
    anneau: string;
    niveau: number | null;
    variation: number;
    points: { trimestre: string; niveau: number | null }[];
    preuve: string | null;
    porteurs: number;
  }[];
  moyennePersonne: { trimestre: string; moyenne: number | null }[];
  moyenneEquipe: { trimestre: string; moyenne: number | null }[];
  journal: { trimestre: string; titre: string; niveau: number; preuve: string | null }[];
  alertes: string[];
};

const LIBELLES_NIVEAU: Record<number, string> = {
  0: 'Never used',
  1: 'Basics',
  2: 'Supervised',
  3: 'Autonomous',
  4: 'Advanced',
  5: 'Reference',
};

function Trajectoire({ personneId }: { personneId: string }): JSX.Element {
  const donnees = useRequete<TrajectoireVue>(`/api/personnes/${personneId}/trajectoire`, [personneId]);

  if (donnees.erreur) return <Message variante="erreur">{donnees.erreur.message}</Message>;
  if (!donnees.donnees) return <Chargement />;

  const t = donnees.donnees;
  const etiquettes = t.trimestres.map((q) => q.replace('-Q', ' Q'));

  return (
    <div className="trajectoire">
      <div className="trajectoire__identite">
        <span className="pastille pastille--grande">{t.personne.initiales}</span>
        <div>
          <h3 style={{ fontSize: 22 }}>{t.personne.nom}</h3>
          <p className="discret" style={{ marginTop: 4 }}>
            {t.personne.intitulePoste ?? 'Role not set'} · {t.personne.email}
          </p>
          <div className="jetons" style={{ marginTop: 8 }}>
            {/* `?? []` : un onglet resté ouvert pendant un déploiement ne doit pas
                écran-blanchir sur un champ que l'API vient d'ajouter. */}
            {(t.personne.equipes ?? []).length === 0 ? (
              <span className="discret">No team</span>
            ) : (
              (t.personne.equipes ?? []).map((e) => (
                <span key={e.id} className="jeton" title={e.libellesTags.join(', ')}>
                  {e.nom}
                </span>
              ))
            )}
          </div>
        </div>
        <Link to={`/personnes/${t.personne.id}`} className="bouton bouton--petit">
          Full profile
        </Link>
      </div>

      <div className="bande-kpis bande-kpis--plate">
        <Kpi valeur={t.kpis.declarees} libelle="Skills declared" />
        <Kpi valeur={t.kpis.moyenne ?? '—'} libelle="Average level" />
        <Kpi valeur={t.kpis.referentDe} libelle="Reference for" />
        <Kpi
          valeur={
            t.kpis.variationMoyenne === null
              ? '—'
              : t.kpis.variationMoyenne > 0
                ? `+${t.kpis.variationMoyenne}`
                : String(t.kpis.variationMoyenne)
          }
          libelle={`Since ${etiquettes[0] ?? '—'}`}
        />
      </div>

      {t.alertes.map((alerte) => (
        <div key={alerte} style={{ marginBottom: 16 }}>
          <Message variante="attention">{alerte}</Message>
        </div>
      ))}

      <section className="trajectoire__competences">
        <h4 className="titre-section">Level per skill · {t.trimestres.length} quarters</h4>
        {t.competences.length === 0 ? (
          <p className="discret">Nothing declared over this window.</p>
        ) : (
          <div>
            {t.competences.slice(0, 18).map((c) => (
              <div key={c.technologieId} className="ligne-competence">
                <span style={{ fontWeight: 500 }}>{c.titre}</span>
                <Sparkline
                  valeurs={c.points.map((p) => p.niveau)}
                  teinte={(c.niveau ?? 0) >= 4 ? '#D6007F' : (c.niveau ?? 0) >= 3 ? '#004F99' : '#C4CADF'}
                  titre={`${c.titre} over time`}
                />
                <BarreNiveau niveau={c.niveau} />
                <span className="ligne-competence__niveau">
                  {c.niveau === null ? '—' : `${c.niveau} · ${LIBELLES_NIVEAU[c.niveau]}`}
                </span>
                {c.porteurs === 1 && (c.niveau ?? 0) >= 4 ? (
                  <Badge variante="risque">sole holder</Badge>
                ) : (
                  <span />
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="deux-colonnes">
        <section className="trajectoire__cote">
          <h4 className="titre-section">Average level over time</h4>
          <GraphiqueLignes
            hauteur={190}
            max={5}
            etiquettes={etiquettes}
            series={[
              {
                cle: 'personne',
                libelle: t.personne.nom.split(' ')[0] ?? 'Person',
                teinte: '#D6007F',
                valeurs: t.moyennePersonne.map((p) => p.moyenne),
              },
              {
                cle: 'equipe',
                libelle: 'Team average',
                teinte: '#C4CADF',
                valeurs: t.moyenneEquipe.map((p) => p.moyenne),
              },
            ]}
          />
          <div className="legende-series">
            <span className="legende__entree">
              <span className="legende__trait" style={{ background: '#D6007F' }} aria-hidden="true" />
              {t.personne.nom.split(' ')[0]}
            </span>
            <span className="legende__entree">
              <span className="legende__trait" style={{ background: '#C4CADF' }} aria-hidden="true" />
              Team average
            </span>
          </div>

          <h4 className="titre-section" style={{ marginTop: 28 }}>
            Evidence log
          </h4>
          {t.journal.length === 0 ? (
            <p className="discret">No level 3 or above declared yet.</p>
          ) : (
            <div>
              {t.journal.map((entree) => (
                <div key={`${entree.trimestre}-${entree.titre}`} className="entree-journal">
                  <span className="mono discret" style={{ fontSize: 12 }}>
                    {entree.trimestre.replace('-Q', ' Q')}
                  </span>
                  <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--fg-2)' }}>
                    {entree.titre} → <strong style={{ color: 'var(--ink)' }}>level {entree.niveau}</strong>.{' '}
                    {entree.preuve ?? 'No evidence recorded.'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
